import { useCallback, useEffect, useRef, useState } from "react"
import { AppState } from "react-native"
import { Camera } from "expo-camera"
import { getRecordingPermissionsAsync, requestRecordingPermissionsAsync } from "expo-audio"
import { CAUSE } from "./deviceCheckRules"
import { checkWeb } from "./webDeviceProbe"
import { IS_WEB } from "../shared/platform"

/**
 * Tente d'ouvrir la caméra et le micro, et renvoie la cause de ce qui s'est
 * passé pour chacun. Ne dit rien du verdict affiché : la traduction en
 * ✅ / ⚠️ / ❌ vit dans `deviceCheckRules`, pour rester testable à part.
 *
 * Le niveau sonore n'est pas mesuré ici — `useMicrophoneLevel` le fait déjà et
 * tourne en parallèle. Ce hook répond seulement à « est-ce que l'appareil
 * s'ouvre, et sinon pourquoi ».
 */

const unchanged = (previous, next) =>
    Object.keys(next).every((key) => previous[key] === next[key])

/** Sur natif, l'OS ne distingue pas « absent » de « occupé » : tout passe par la permission. */
function fromNativeStatus(status) {
    if (status === "granted") return CAUSE.GRANTED
    if (status === "undetermined") return CAUSE.NOT_ASKED
    return CAUSE.DENIED
}

async function checkNative(shouldRequest) {
    const askCamera = shouldRequest
        ? Camera.requestCameraPermissionsAsync
        : Camera.getCameraPermissionsAsync
    const askAudio = shouldRequest
        ? requestRecordingPermissionsAsync
        : getRecordingPermissionsAsync

    const [cam, mic] = await Promise.all([askCamera(), askAudio()])
    return {
        camera: fromNativeStatus(cam?.status),
        microphone: fromNativeStatus(mic?.status),
    }
}

export const useDeviceCheck = () => {
    const [causes, setCauses] = useState({
        camera: CAUSE.NOT_ASKED,
        microphone: CAUSE.NOT_ASKED,
    })
    const [isChecking, setIsChecking] = useState(false)
    const mounted = useRef(true)

    const run = useCallback(async ({ request = true, audioDeviceId, videoDeviceId, only } = {}) => {
        setIsChecking(true)
        try {
            // `only` ne renseigne qu'un appareil : on fusionne au lieu de
            // remplacer, sinon une sonde ciblée effacerait ce qu'on sait de l'autre.
            const next = IS_WEB
                ? await checkWeb({ audioDeviceId, videoDeviceId, only, request })
                : await checkNative(request)
            // Même objet, même rendu : la re-vérification périodique renvoie le
            // plus souvent exactement ce qu'on savait déjà, et remplacer l'état
            // par une copie identique re-rendait tout le panneau pour rien.
            if (mounted.current) setCauses((previous) => (unchanged(previous, next) ? previous : { ...previous, ...next }))
            return next
        } catch (error) {
            // Une erreur inattendue ne doit jamais empêcher d'entrer en live :
            // on reste sur « inconnu », l'écran laisse le bouton accessible.
            const next = { camera: CAUSE.UNKNOWN, microphone: CAUSE.UNKNOWN }
            if (mounted.current) setCauses(next)
            return next
        } finally {
            if (mounted.current) setIsChecking(false)
        }
    }, [])

    useEffect(() => {
        mounted.current = true
        // Premier passage sans rien demander : on veut savoir où on en est
        // avant de solliciter l'élève.
        run({ request: false })
        return () => {
            mounted.current = false
        }
    }, [run])

    // Sur mobile, l'élève part dans les réglages système pour réactiver un accès.
    // On revérifie tout seul à son retour, pour qu'il n'ait pas à chercher un bouton.
    useEffect(() => {
        if (IS_WEB) return undefined
        const subscription = AppState.addEventListener("change", (state) => {
            if (state === "active") run({ request: false })
        })
        return () => subscription?.remove()
    }, [run])

    return { causes, isChecking, run }
}
